// src/pages/LiveStreams/LiveStreamWatchPage.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { 
  ArrowLeft, 
  Heart, 
  MessageCircle, 
  Share2, 
  Users,
  Eye,
  Send,
  Radio,
  Play,
  Pause,
  Settings,
  Maximize,
  Volume2,
  VolumeX,
  AlertCircle,
  Clock,
  Sparkles,
  Wifi,
  WifiOff
} from 'lucide-react';
import type { RootState } from '../../store';
import { uiActions } from '../../store/reducers/uiReducers';
import MainLayout from '../../components/MainLayout/MainLayout';
import LoginPromptModal from '../../components/Auth/LoginPromptModal';
import liveStreamService, { type LiveStream, type LiveStreamChatMessage } from '../../services/liveStreamService';
import { useLiveStreamWebSocket, type ChatMessage, type ReactionEvent, type StreamStats, type ViewerEvent } from '../../hooks/useLiveStreamWebSocket';
import { useWebRTC } from '../../hooks/useWebRTC';
import { useHlsPlayback } from '../../hooks/useHlsPlayback';

// Define reaction types
type ReactionType = ReactionEvent['reactionType'];

// Replay Player Component for recorded videos (WebM, MP4)
const REPLAY_PREVIEW_DURATION = 90; // seconds

// Exported for its own test. It carries the HLS/progressive decision for replays, which
// is worth testing directly rather than through the whole watch page and its services.
export const ReplayPlayer = ({ 
  src,
  deliveryFormat,
  onPlay,
  onPause,
  previewExpired,
  onTimeChange,
  fallbackDuration = 0,
}: { 
  src: string;
  /**
   * What `src` actually is, as stated by the server rather than guessed here.
   *
   * Replays now go to the Bunny Stream library, so this is usually an HLS manifest.
   * Safari plays HLS natively and Chrome and Firefox do not, so without the hook below
   * a replay would play only on a Mac - the same bug that stopped the homepage hero.
   */
  deliveryFormat?: 'HLS' | 'PROGRESSIVE';
  onPlay?: () => void;
  onPause?: () => void;
  previewExpired?: boolean;
  onTimeChange?: (time: number) => void;
  fallbackDuration?: number;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isHls = deliveryFormat === 'HLS';

  // The hook owns the source for a manifest. It is a no-op for a progressive file, which
  // keeps using <source> below so the browser gets its MIME hint.
  useHlsPlayback(videoRef, isHls ? src : undefined, deliveryFormat);

  // MIME hint for a progressive file only. Never derived for a manifest: a Bunny Stream
  // URL carries its token in a path segment ahead of the filename, so this guess would
  // land on video/webm for an HLS playlist.
  const sourceType = src.endsWith('.mp4') ? 'video/mp4' : 'video/webm';

  // Validate src on mount (no addEventListener — React JSX props handle events)
  useEffect(() => {
    if (!src) {
      setError('No recording URL provided');
    }
  }, [src]);

  // Poll for duration discovery for WebM files with unknown duration.
  // WebM from MediaRecorder may initially report Infinity; the browser
  // often resolves it as more data loads. We just poll rather than
  // seeking (seeking to MAX_SAFE_INTEGER triggers timeupdate with huge
  // values that break the preview timer).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || duration > 0) return;

    const interval = setInterval(() => {
      if (video.duration && isFinite(video.duration) && video.duration > 0) {
        setDuration(video.duration);
        clearInterval(interval);
      }
    }, 500);

    // If polling doesn't discover duration within 5s, use fallback
    const fallbackTimer = setTimeout(() => {
      if (duration === 0 && fallbackDuration > 0) {
        setDuration(fallbackDuration);
      }
    }, 5000);

    return () => {
      clearInterval(interval);
      clearTimeout(fallbackTimer);
    };
  }, [duration, fallbackDuration]);

  // Pause video when preview expires
  useEffect(() => {
    if (previewExpired && videoRef.current && !videoRef.current.paused) {
      videoRef.current.pause();
    }
  }, [previewExpired]);

  // Event handlers as JSX props — React attaches these before the element
  // receives any events, avoiding the race condition with addEventListener.
  const handleLoadedMetadata = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setError(null);
      // WebM recordings from MediaRecorder often report Infinity/NaN for duration
      if (isFinite(video.duration) && video.duration > 0) {
        setDuration(video.duration);
      }
    }
  }, []);

  // Fires when browser discovers the actual duration (important for WebM)
  const handleDurationChange = useCallback(() => {
    const video = videoRef.current;
    if (video && isFinite(video.duration) && video.duration > 0) {
      setDuration(video.duration);
    }
  }, []);

  const handleTimeUpdate = useCallback(() => {
    const video = videoRef.current;
    if (video) {
      setCurrentTime(video.currentTime);
      onTimeChange?.(video.currentTime);
      // Also try to pick up duration if it wasn't available at loadedmetadata
      if (duration === 0 && isFinite(video.duration) && video.duration > 0) {
        setDuration(video.duration);
      }
    }
  }, [onTimeChange, duration]);

  const handleVideoError = useCallback(() => {
    const video = videoRef.current;
    const mediaError = video?.error;
    let errorMessage = 'Failed to load recording.';

    if (mediaError) {
      switch (mediaError.code) {
        case MediaError.MEDIA_ERR_ABORTED:
          errorMessage = 'Video playback was aborted.';
          break;
        case MediaError.MEDIA_ERR_NETWORK:
          errorMessage = 'Network error while loading video. Please check your connection.';
          break;
        case MediaError.MEDIA_ERR_DECODE:
          errorMessage = 'Video format error. The recording may be corrupted.';
          break;
        case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
          errorMessage = 'Video format not supported. The recording may be unavailable.';
          break;
        default:
          errorMessage = `Unknown error (code: ${mediaError.code})`;
      }
    }

    setError(errorMessage);
  }, []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    onPlay?.();
  }, [onPlay]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    onPause?.();
  }, [onPause]);

  const togglePlay = useCallback(() => {
    const video = videoRef.current;
    if (!video || previewExpired) return;

    if (isPlaying) {
      video.pause();
    } else {
      // Guard: don't call play() if the source hasn't loaded at all
      if (video.readyState === 0 && video.error) {
        setError('Unable to play recording. The video source failed to load.');
        return;
      }
      video.play().catch(err => {
        // AbortError is expected when play() is interrupted by pause() — ignore it
        if (err.name === 'AbortError') return;
        console.error('Failed to play video:', err);
        setError('Unable to play recording.');
      });
    }
  }, [isPlaying, previewExpired]);

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleProgressClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const video = videoRef.current;
    if (!video || previewExpired) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    let newTime = pos * (effectiveDuration || 0);
    // Block seeking past preview limit for unauthenticated users
    if (onTimeChange && newTime >= REPLAY_PREVIEW_DURATION) {
      newTime = REPLAY_PREVIEW_DURATION - 1;
    }
    video.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  const formatTime = (time: number) => {
    // Handle invalid values
    if (!isFinite(time) || isNaN(time) || time < 0) {
      return '0:00';
    }
    const hours = Math.floor(time / 3600);
    const mins = Math.floor((time % 3600) / 60);
    const secs = Math.floor(time % 60);
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // The effective duration: use video's native duration if available, otherwise fallback
  const effectiveDuration = duration > 0 ? duration : fallbackDuration;

  return (
    <div 
      className="relative aspect-video bg-black rounded-lg overflow-hidden group"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      <video
        ref={videoRef}
        className={`w-full h-full object-contain`}
        playsInline
        preload="auto"
        onLoadedMetadata={handleLoadedMetadata}
        onDurationChange={handleDurationChange}
        onTimeUpdate={handleTimeUpdate}
        onError={handleVideoError}
        onPlay={handlePlay}
        onPause={handlePause}
        onClick={togglePlay}
      >
        {/* A manifest is attached by useHlsPlayback; a <source> here would make the
            browser try to load the playlist as a media file. */}
        {!isHls && <source src={src} type={sourceType} />}
      </video>


      {/* Ad Playing Indicator */}
      
      {/* Error Display */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center p-6 max-w-md">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <p className="text-white mb-2">Failed to load recording</p>
            <p className="text-gray-400 text-sm">{error}</p>
          </div>
        </div>
      )}
      
      {/* Replay Badge */}
      <div className="absolute top-4 left-4 px-3 py-1.5 bg-gray-900/80 text-white text-sm font-medium rounded flex items-center gap-2">
        <Play className="w-4 h-4" />
        REPLAY
      </div>
      
      {/* Video Controls */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          {/* Progress Bar */}
          <div 
            className="w-full h-2 bg-gray-600 rounded-full cursor-pointer mb-4 relative"
            onClick={handleProgressClick}
          >
            <div 
              className="h-full bg-orange-600 rounded-full relative"
              style={{ width: `${effectiveDuration ? (currentTime / effectiveDuration) * 100 : 0}%` }}
            >
              <div className="absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full shadow-lg" />
            </div>
          </div>
        
          <div className="flex items-center justify-between">
            {/* Left Controls */}
            <div className="flex items-center space-x-4">
              <button
                onClick={togglePlay}
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-full p-3 transition-colors"
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>
            
              {/* Volume */}
              <button
                onClick={toggleMute}
                className="text-white hover:text-orange-400"
              >
                {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-24"
              />
            </div>
          
            {/* Right Controls */}
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2 text-sm">
                <span>{formatTime(currentTime)}</span>
                <span>/</span>
                <span>{formatTime(effectiveDuration)}</span>
              </div>
              <button className="text-white hover:text-orange-400">
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={toggleFullscreen}
                className="text-white hover:text-orange-400"
              >
                <Maximize className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Preview Login Wall for replays */}
      {previewExpired && (
        <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/85 backdrop-blur-sm">
          <div className="text-center max-w-md mx-4 p-8">
            <div className="w-20 h-20 bg-orange-600/30 rounded-full flex items-center justify-center mx-auto mb-6">
              <Play className="w-10 h-10 text-orange-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-3">Enjoying the replay?</h2>
            <p className="text-gray-300 mb-6">Sign in to continue watching and unlock all content on StrymHub.</p>
            <div className="space-y-3">
              <a href="/login" className="block w-full px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium text-center">Sign In to Continue</a>
              <a href="/signup" className="block w-full px-6 py-3 bg-gray-800 text-gray-300 rounded-lg hover:bg-gray-700 transition-colors font-medium text-center">Create Free Account</a>
            </div>
            <p className="text-gray-500 text-sm mt-4">It&apos;s free — sign up in seconds.</p>
          </div>
        </div>
      )}

      {/* Center play button */}
      {!isPlaying && !error && !previewExpired && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/40 transition-colors"
        >
          <div className="bg-white/90 hover:bg-white rounded-full p-6">
            <Play className="w-12 h-12 text-black ml-1" />
          </div>
        </button>
      )}
    </div>
  );
};

// Chat Message Component
const ChatMessageItem = ({ message, isHost }: { message: ChatMessage | LiveStreamChatMessage; isHost: boolean }) => {
  const displayName = message.username;
  const isFromHost = 'isFromHost' in message ? message.isFromHost : isHost;
  
  return (
    <div className={`flex gap-2 p-2 rounded-lg ${isFromHost ? 'bg-orange-500/10' : 'hover:bg-white/5'} transition-colors`}>
      <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center text-xs font-medium ${
        isFromHost 
          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
          : 'bg-gray-700 text-gray-300'
      }`}>
        {displayName.charAt(0).toUpperCase()}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className={`text-sm font-medium truncate ${isFromHost ? 'text-orange-400' : 'text-gray-300'}`}>
            {displayName}
          </span>
          {isFromHost && (
            <span className="text-[10px] px-1.5 py-0.5 bg-orange-500 text-white rounded font-medium">
              HOST
            </span>
          )}
        </div>
        <p className="text-sm text-gray-400 break-words">{message.message}</p>
      </div>
    </div>
  );
};

// Reactions Display Component
const ReactionsOverlay = ({ reactions }: { reactions: Array<{ type: ReactionType; id: string }> }) => {
  const reactionEmojis: Record<ReactionType, string> = {
    LIKE: '❤️',
    HEART: '💖',
    WOW: '😮',
    LAUGH: '😂',
    CLAP: '👏',
    FIRE: '🔥',
    SAD: '😢',
    };

  return (
    <div className="absolute bottom-20 right-4 pointer-events-none z-50">
      {reactions.slice(-20).map((reaction) => (
        <span
          key={reaction.id}
          className="absolute animate-float-up text-2xl"
          style={{
            left: `${Math.random() * 100}px`,
            animationDelay: `${Math.random() * 0.5}s`,
          }}
        >
          {reactionEmojis[reaction.type]}
        </span>
      ))}
    </div>
  );
};

// Broadcaster Preview Component - shows local video for the host
const BroadcasterPreview = ({ 
  streamId,
  isLive 
}: { 
  streamId: string;
  isLive: boolean;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true); // Muted by default for self-view
  const [showControls, setShowControls] = useState(true);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Get local camera/mic stream
  useEffect(() => {
    const initMedia = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            width: { ideal: 1920 },
            height: { ideal: 1080 },
            facingMode: 'user'
          },
          audio: true
        });
        
        setMediaStream(stream);
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          videoRef.current.muted = true; // Always mute self-view to prevent echo
          videoRef.current.play().catch(() => {}); // Suppress autoplay error
        }
      } catch (err) {
        console.error('Failed to get local media:', err);
        setError('Failed to access camera/microphone');
      }
    };

    initMedia();

    return () => {
      if (mediaStream) {
        mediaStream.getTracks().forEach(track => track.stop());
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [streamId]);

  const toggleMute = () => {
    if (mediaStream) {
      mediaStream.getAudioTracks().forEach(track => {
        track.enabled = isMuted;
      });
      setIsMuted(!isMuted);
    }
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  return (
    <div 
      className="relative aspect-video bg-black rounded-lg overflow-hidden group"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        playsInline
        autoPlay
        muted
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      
      {/* Broadcaster indicator */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
        <span className="px-2 py-1 bg-orange-600/80 text-white text-xs rounded flex items-center gap-1">
          <Eye className="w-3 h-3" />
          Your Preview
        </span>
      </div>
      
      {/* Error Display */}
      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <p className="text-white font-medium">{error}</p>
            <p className="text-gray-400 text-sm mt-2">
              Please check your camera/microphone permissions
            </p>
          </div>
        </div>
      )}
      
      {/* Live Indicator */}
      {isLive && (
        <div className="absolute top-4 left-4 px-3 py-1.5 bg-red-600 text-white text-sm font-bold rounded flex items-center gap-2">
          <Radio className="w-4 h-4 animate-pulse" />
          LIVE (Broadcasting)
        </div>
      )}
      
      {/* Video Controls */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Mute indicator for self-view */}
              <button
                onClick={toggleMute}
                className="text-white hover:text-orange-400"
                title={isMuted ? 'Your audio is muted (viewers can hear you)' : 'Audio enabled'}
              >
                {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
              </button>
              <span className="text-xs text-gray-400">
                {isMuted ? 'Self-muted' : 'Monitoring audio'}
              </span>
            
              {isLive && (
                <span className="text-red-400 text-sm font-medium flex items-center gap-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  Broadcasting
                </span>
              )}
            </div>
          
            <div className="flex items-center space-x-4">
              <button className="text-white hover:text-orange-400">
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={toggleFullscreen}
                className="text-white hover:text-orange-400"
              >
                <Maximize className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// WebRTC Video Player Component for real-time streaming
const WebRTCPlayer = ({ 
  streamId,
  isLive,
  onConnectionChange,
}: { 
  streamId: string;
  isLive: boolean;
  onConnectionChange?: (connected: boolean) => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const [showControls, setShowControls] = useState(true);
  const [connectionError, setConnectionError] = useState<string | null>(null);
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const watchTimeRef = useRef(0);
  const watchTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Handle remote stream from WebRTC
  const handleRemoteStream = useCallback((remoteStream: MediaStream) => {
    if (videoRef.current) {
      videoRef.current.srcObject = remoteStream;
      videoRef.current.play().catch(() => {}); // Suppress autoplay errors

      // Initialize ads once we have a stream (use virtual duration for live)
    }
  }, []);

  // Track watch time and check for mid-roll ads
  useEffect(() => {
    if (isPlaying) {
      watchTimerRef.current = setInterval(() => {
        watchTimeRef.current += 1;
      }, 1000);
    } else if (watchTimerRef.current) {
      clearInterval(watchTimerRef.current);
      watchTimerRef.current = null;
    }

    return () => {
      if (watchTimerRef.current) {
        clearInterval(watchTimerRef.current);
        watchTimerRef.current = null;
      }
    };
  }, [isPlaying]);

  const handleConnectionStateChange = useCallback((state: RTCPeerConnectionState) => {
    onConnectionChange?.(state === 'connected');
    
    if (state === 'failed') {
      setConnectionError('Connection failed. The broadcaster may have disconnected.');
    } else if (state === 'connected') {
      setConnectionError(null);
    }
  }, [onConnectionChange]);

  const handleWebRTCError = useCallback((error: Error) => {
    console.error('[WebRTCPlayer] WebRTC error:', error);
    setConnectionError(error.message);
  }, []);

  const { isConnected, connectionState, requestStream } = useWebRTC({
    streamId,
    isBroadcaster: false,
    onRemoteStream: handleRemoteStream,
    onConnectionStateChange: handleConnectionStateChange,
    onError: handleWebRTCError,
  });

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (isPlaying) {
      video.pause();
      setIsPlaying(false);
    } else {
      video.play().catch(err => {
        console.error('Failed to play video:', err);
      });
      setIsPlaying(true);
    }
  };

  const toggleMute = () => {
    const video = videoRef.current;
    if (!video) return;
    
    video.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const video = videoRef.current;
    if (!video) return;
    
    const newVolume = parseFloat(e.target.value);
    video.volume = newVolume;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const toggleFullscreen = () => {
    const video = videoRef.current;
    if (!video) return;
    
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      video.requestFullscreen();
    }
  };

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    controlsTimeoutRef.current = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  return (
    <div 
      className="relative aspect-video bg-black rounded-lg overflow-hidden group"
      onMouseMove={handleMouseMove}
      onMouseLeave={() => isPlaying && setShowControls(false)}
    >
      <video
        ref={videoRef}
        className="w-full h-full object-contain"
        playsInline
        autoPlay
        onClick={togglePlay}
      />


      {/* Ad Playing Indicator */}
      
      {/* Connection Status */}
      <div className="absolute top-4 right-4 flex items-center gap-2">
          {isConnected ? (
            <span className="px-2 py-1 bg-green-600/80 text-white text-xs rounded flex items-center gap-1">
              <Wifi className="w-3 h-3" />
              Connected
            </span>
          ) : (
            <span className="px-2 py-1 bg-yellow-600/80 text-white text-xs rounded flex items-center gap-1">
              <WifiOff className="w-3 h-3" />
              {connectionState === 'connecting' ? 'Connecting...' : 'Waiting for stream'}
            </span>
          )}
        </div>
      
      {/* Error Display */}
      {connectionError && (
        <div className="absolute top-12 right-4 px-3 py-1.5 bg-red-600/80 text-white text-sm rounded flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {connectionError}
        </div>
      )}
      
      {/* Live Indicator */}
      {isLive && (
        <div className="absolute top-4 left-4 px-3 py-1.5 bg-red-600 text-white text-sm font-bold rounded flex items-center gap-2">
          <Radio className="w-4 h-4 animate-pulse" />
          LIVE
        </div>
      )}
      
      {/* Video Controls */}
      <div className={`absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent transition-opacity duration-300 ${showControls ? 'opacity-100' : 'opacity-0'}`}>
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={togglePlay}
                className="bg-orange-600 hover:bg-orange-700 text-white rounded-full p-3 transition-colors"
              >
                {isPlaying ? <Pause className="w-6 h-6" /> : <Play className="w-6 h-6" />}
              </button>
            
              <button
                onClick={toggleMute}
                className="text-white hover:text-orange-400"
              >
                {isMuted ? <VolumeX className="w-6 h-6" /> : <Volume2 className="w-6 h-6" />}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.01"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="w-24"
              />
            
              {isLive && (
                <span className="text-red-400 text-sm font-medium flex items-center gap-1">
                  <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                  Live
                </span>
              )}
            </div>
          
            <div className="flex items-center space-x-4">
              <button
                onClick={() => requestStream()}
                className="text-white hover:text-orange-400 text-xs"
                title="Reconnect"
              >
                Reconnect
              </button>
              <button className="text-white hover:text-orange-400">
                <Settings className="w-5 h-5" />
              </button>
              <button
                onClick={toggleFullscreen}
                className="text-white hover:text-orange-400"
              >
                <Maximize className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Waiting for stream overlay */}
      {!isPlaying && !connectionError && (
        <button
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/40 transition-colors"
        >
          {isConnected ? (
            <div className="bg-white/90 hover:bg-white rounded-full p-6">
              <Play className="w-12 h-12 text-black ml-1" />
            </div>
          ) : (
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
              <p className="text-white">Connecting to stream...</p>
            </div>
          )}
        </button>
      )}
    </div>
  );
};

const LiveStreamWatchPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((state: RootState) => state.user.isAuthenticated);
  const currentUser = useSelector((state: RootState) => state.user.userInfo);
  
  const [stream, setStream] = useState<LiveStream | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [chatMessage, setChatMessage] = useState('');
  const [historicalChat, setHistoricalChat] = useState<LiveStreamChatMessage[]>([]);
  const [liveChatMessages, setLiveChatMessages] = useState<ChatMessage[]>([]);
  const [senderNotice, setSenderNotice] = useState<string | null>(null);
  const [visibleReactions, setVisibleReactions] = useState<Array<{ type: ReactionType; id: string }>>([]);
  const [currentStats, setCurrentStats] = useState<StreamStats | null>(null);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [loginPromptMessage, setLoginPromptMessage] = useState('');
  const [replayPreviewExpired, setReplayPreviewExpired] = useState(false);
  
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const reactionIdCounter = useRef(0);
  const previewTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const replayTimeRef = useRef(0);

  // Callback handlers for WebSocket events
  const handleStatsUpdate = useCallback((stats: StreamStats) => {
    setCurrentStats(stats);
  }, []);

  const handleChatMessage = useCallback((message: ChatMessage) => {
    setLiveChatMessages(prev => [...prev, message]);
  }, []);

  /**
   * A message the server addressed to this viewer alone, not to the room.
   *
   * Refusals: too young for this stream's chat, chat turned off, not signed in. They used
   * to be broadcast to everybody through the handler's @SendTo, which meant the room read
   * them and the person refused - who is gated out of the chat topic - did not.
   *
   * Shown by the chat box rather than as a toast, because that is where the person just
   * acted and where the explanation makes sense. It replaces any previous one: the current
   * reason is the only one worth reading.
   */
  const handleSenderNotice = useCallback((message: ChatMessage) => {
    setSenderNotice(message.message ?? null);
  }, []);

  const handleReaction = useCallback((reaction: ReactionEvent) => {
    const newReaction = {
      type: reaction.reactionType,
      id: `${++reactionIdCounter.current}`
    };
    
    setVisibleReactions(prev => [...prev, newReaction]);
    
    // Remove after animation
    setTimeout(() => {
      setVisibleReactions(prev => prev.filter(r => r.id !== newReaction.id));
    }, 2000);
  }, []);

  const handleViewerUpdate = useCallback((_event: ViewerEvent) => {
    // Stats will be updated via stats subscription
  }, []);

  // Initialize WebSocket connection when stream is loaded - only for LIVE streams
  const {
    isConnected,
    sendChatMessage,
    sendReaction,
    joinStream,
    leaveStream
  } = useLiveStreamWebSocket({
    streamId: stream?.status === 'LIVE' ? stream.id : null, // Only connect for live streams
    onStatsUpdate: handleStatsUpdate,
    onChatMessage: handleChatMessage,
    onReaction: handleReaction,
    onViewerUpdate: handleViewerUpdate,
    onSenderNotice: handleSenderNotice,
    autoJoin: true
  });

  // Fetch stream data
  useEffect(() => {
    const fetchStream = async () => {
      if (!slug) {
        setError('Invalid stream');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const streamData = await liveStreamService.getStreamBySlug(slug);
        
        if (!streamData) {
          setError('Stream not found');
          return;
        }
        
        setStream(streamData);
        
        // Fetch historical chat messages
        if (streamData.isChatEnabled) {
          const chatHistory = await liveStreamService.getLatestChatMessages(streamData.id, 100);
          setHistoricalChat(chatHistory);
        }
      } catch (err) {
        // The server's own words. A 403 here is usually the age gate - "Please add your
        // date of birth to your profile to watch age-restricted content" - which is
        // something the viewer can act on. Replacing it with "Failed to load stream",
        // under a heading that says the stream does not exist or has ended, told them
        // the opposite of what was true and gave them nothing to do about it.
        console.error('Error fetching stream:', err);
        setError(err instanceof Error ? err.message : 'Failed to load stream');
      } finally {
        setLoading(false);
      }
    };

    fetchStream();
  }, [slug]);

  // Join/leave stream when connected
  useEffect(() => {
    if (stream && isConnected) {
      joinStream();
      
      return () => {
        leaveStream();
      };
    }
  }, [stream, isConnected, joinStream, leaveStream]);

  // Auto-scroll chat
  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [liveChatMessages, historicalChat]);

  // 90-second preview enforcement for replay (unauthenticated)
  const handleReplayTimeChange = useCallback((time: number) => {
    replayTimeRef.current = time;
    if (!isAuthenticated && time >= REPLAY_PREVIEW_DURATION && !replayPreviewExpired) {
      setReplayPreviewExpired(true);
    }
  }, [isAuthenticated, replayPreviewExpired]);

  // Also run a timer-based check (handles continuous playback)
  useEffect(() => {
    if (isAuthenticated || replayPreviewExpired) return;
    // Only for replays
    if (!stream || stream.status !== 'ENDED' || !stream.recordingUrl) return;

    // Clear any existing timer
    if (previewTimerRef.current) {
      clearTimeout(previewTimerRef.current);
      previewTimerRef.current = null;
    }

    const remaining = Math.max(0, REPLAY_PREVIEW_DURATION - replayTimeRef.current);
    if (remaining <= 0) {
      setReplayPreviewExpired(true);
      return;
    }

    previewTimerRef.current = setTimeout(() => {
      setReplayPreviewExpired(true);
    }, remaining * 1000);

    return () => {
      if (previewTimerRef.current) {
        clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
      }
    };
  }, [isAuthenticated, replayPreviewExpired, stream]);

  // Helper: require auth for interactive actions
  const requireAuth = useCallback((action: string): boolean => {
    if (isAuthenticated) return true;
    setLoginPromptMessage(`Sign in to ${action}.`);
    setShowLoginPrompt(true);
    return false;
  }, [isAuthenticated]);

  const handleSendChat = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!requireAuth('chat')) return;
    
    if (!chatMessage.trim() || !stream) return;
    
    // For live streams, use WebSocket; for replays, use REST API
    if (stream.status === 'LIVE' && isConnected) {
      sendChatMessage(chatMessage.trim());
      setChatMessage('');
    } else {
      // Use REST API for ended/replay streams
      try {
        const newChat = await liveStreamService.sendChatMessage(stream.id, chatMessage.trim());
        // Add to local chat display
        setHistoricalChat(prev => [...prev, newChat]);
        setChatMessage('');
        dispatch(uiActions.addNotification({
          type: 'success',
          message: 'Comment added!'
        }));
      } catch (err) {
        console.error('Failed to send chat:', err);
        dispatch(uiActions.addNotification({
          type: 'error',
          message: 'Failed to send message'
        }));
      }
    }
  }, [chatMessage, requireAuth, isConnected, sendChatMessage, dispatch, stream]);

  const handleReactionClick = useCallback(async (type: ReactionType) => {
    if (!requireAuth('react to this stream')) return;
    
    if (!stream) return;
    
    // Show immediate visual feedback
    const newReaction = {
      type,
      id: `local-${++reactionIdCounter.current}`
    };
    setVisibleReactions(prev => [...prev, newReaction]);
    setTimeout(() => {
      setVisibleReactions(prev => prev.filter(r => r.id !== newReaction.id));
    }, 2000);
    
    // For live streams, use WebSocket; for replays, use REST API
    if (stream.status === 'LIVE' && isConnected) {
      sendReaction(type, true);
    } else if (type === 'LIKE') {
      // Use REST API for likes on ended/replay streams
      try {
        const updatedStream = await liveStreamService.likeStream(stream.id);
        setStream(updatedStream);
      } catch (err) {
        console.error('Failed to like stream:', err);
        dispatch(uiActions.addNotification({
          type: 'error',
          message: 'Failed to like stream'
        }));
      }
    }
  }, [requireAuth, isConnected, sendReaction, dispatch, stream]);

  const handleShare = async () => {
    const shareUrl = window.location.href;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: stream?.title || 'Live Stream',
          url: shareUrl
        });
      } catch {
        // User cancelled or share failed
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      dispatch(uiActions.addNotification({
        type: 'success',
        message: 'Link copied to clipboard!'
      }));
    }
  };

  // Combine historical and live chat messages
  const allChatMessages = [...historicalChat, ...liveChatMessages];
  
  // Use WebSocket stats if available, otherwise stream data
  const currentViewers = currentStats?.currentViewers ?? stream?.currentViewers ?? 0;
  const currentLikes = currentStats?.likeCount ?? stream?.likeCount ?? 0;

  if (loading) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-400">Loading stream...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error || !stream) {
    return (
      <MainLayout>
        <div className="min-h-screen bg-gray-900 flex items-center justify-center">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            {/* The server's reason, when there is one. The fixed sentence below used to
                print underneath it and contradict it: an age-gated stream exists and has
                not ended, and telling the viewer otherwise sent them away from the one
                thing that would have let them watch - adding their date of birth. */}
            <h2 className="text-xl font-semibold text-white mb-2">
              {error ? 'Stream unavailable' : 'Stream Not Found'}
            </h2>
            <p className="text-gray-400 mb-4">
              {error || "The stream you're looking for doesn't exist or has ended."}
            </p>
            {/* The way out, when there is one. A signed-out visitor refused by the age
                gate is being asked to sign in, and sending them back to the listing is
                sending them back to the thing they just clicked. */}
            {!isAuthenticated && error && (
              <Link
                to="/login"
                className="mb-3 inline-flex items-center gap-2 rounded-lg bg-orange-600 px-4 py-2 text-white transition-colors hover:bg-orange-700"
              >
                Sign in
              </Link>
            )}
            <Link
              to="/live"
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                !isAuthenticated && error
                  ? 'border border-gray-600 text-gray-300 hover:bg-gray-800'
                  : 'bg-orange-600 text-white hover:bg-orange-700'
              }`}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Live Streams
            </Link>
          </div>
        </div>
      </MainLayout>
    );
  }

  const isLive = stream.status === 'LIVE';
  const isReplay = stream.status === 'ENDED' && stream.recordingUrl;
  
  // Check if current user is the broadcaster/host
  const isBroadcaster = currentUser && stream.host && currentUser.id === stream.host.id;

  return (
    <MainLayout>
      <div className="min-h-screen bg-gray-900 text-white">
        <div className="container mx-auto px-4 py-4">
          {/* Back Button */}
          <button
            onClick={() => navigate('/live')}
            className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back
          </button>
        </div>
        
        <div className="container mx-auto px-4 pb-8">
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
            
            {/* Main Content - Video Player */}
            <div className="xl:col-span-2 space-y-4">
              {/* Video Player - BroadcasterPreview for host, WebRTC for viewers, ReplayPlayer for recordings */}
              <div className="bg-black rounded-lg overflow-hidden relative group mb-6">
                {isLive && isBroadcaster ? (
                  <BroadcasterPreview 
                    streamId={stream.id}
                    isLive={true}
                  />
                ) : isLive ? (
                  <WebRTCPlayer 
                    streamId={stream.id}
                    isLive={true}
                  />
                ) : isReplay && stream.recordingUrl ? (
                  // Replays are HLS from the Bunny Stream library, or a progressive file
                  // for anything recorded before that. The server says which.
                  <ReplayPlayer 
                    src={stream.recordingUrl}
                    deliveryFormat={stream.replayDeliveryFormat}
                    previewExpired={replayPreviewExpired}
                    onTimeChange={handleReplayTimeChange}
                    fallbackDuration={
                      stream.actualStartTime && stream.endedAt
                        ? (new Date(stream.endedAt).getTime() - new Date(stream.actualStartTime).getTime()) / 1000
                        : 0
                    }
                  />
                ) : (
                  <div className="aspect-video bg-gray-800 rounded-lg flex items-center justify-center">
                    <div className="text-center">
                      {stream.status === 'SCHEDULED' ? (
                        <>
                          <Clock className="w-16 h-16 text-orange-400 mx-auto mb-4" />
                          <p className="text-white font-medium">Stream Scheduled</p>
                          {stream.scheduledStartTime && (
                            <p className="text-gray-400 text-sm mt-2">
                              {new Date(stream.scheduledStartTime).toLocaleString()}
                            </p>
                          )}
                        </>
                      ) : stream.status === 'ENDED' ? (
                        <>
                          <Play className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                          <p className="text-gray-400">Stream has ended</p>
                          {!stream.recordingUrl && (
                            <p className="text-gray-500 text-sm mt-2">No recording available</p>
                          )}
                        </>
                      ) : (
                        <>
                          <Radio className="w-16 h-16 text-gray-500 mx-auto mb-4" />
                          <p className="text-gray-400">Waiting for stream to start...</p>
                        </>
                      )}
                    </div>
                  </div>
                )}
                
                {/* Floating Reactions */}
                <ReactionsOverlay reactions={visibleReactions} />
              </div>
              
              {/* Stream Info */}
              <div className="bg-gray-900 rounded-lg p-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h1 className="text-2xl font-bold text-white mb-2">
                      {stream.title}
                    </h1>
                    
                    {/* Host Info */}
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center text-white font-medium">
                        {stream.host.firstName?.charAt(0) || stream.host.username.charAt(0)}
                      </div>
                      <div>
                        <p className="text-white font-medium">
                          {stream.host.firstName && stream.host.lastName 
                            ? `${stream.host.firstName} ${stream.host.lastName}`
                            : stream.host.username}
                        </p>
                        <p className="text-gray-400 text-sm">Host</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Stats & Actions */}
                  <div className="flex flex-col items-end gap-2">
                    {isLive && (
                      <div className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1 text-red-400">
                          <Eye className="w-4 h-4" />
                          {liveStreamService.formatViewerCount(currentViewers)} watching
                        </span>
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleReactionClick('LIKE')}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                      >
                        <Heart className="w-4 h-4" />
                        <span className="text-sm">{liveStreamService.formatViewerCount(currentLikes)}</span>
                      </button>
                      <button
                        onClick={handleShare}
                        className="flex items-center gap-1 px-3 py-1.5 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors"
                      >
                        <Share2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Quick Reactions */}
                {isLive && (
                  <div className="flex items-center gap-2 pt-3 border-t border-gray-700">
                    <span className="text-gray-400 text-sm mr-2">React:</span>
                    {(['LIKE', 'HEART', 'WOW', 'LAUGH', 'CLAP', 'FIRE'] as ReactionType[]).map((type) => {
                      const emojis: Record<ReactionType, string> = {
                        LIKE: '❤️',
                        HEART: '💖',
                        WOW: '😮',
                        LAUGH: '😂',
                        CLAP: '👏',
                        FIRE: '🔥',
                        SAD: '😢',
                      };
                      return (
                        <button
                          key={type}
                          onClick={() => handleReactionClick(type)}
                          className="w-10 h-10 rounded-full bg-gray-800 hover:bg-gray-700 hover:scale-110 transition-all text-xl"
                        >
                          {emojis[type]}
                        </button>
                      );
                    })}
                  </div>
                )}
                
                {/* Description */}
                {stream.description && (
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <p className="text-gray-400 text-sm whitespace-pre-wrap">{stream.description}</p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Chat Sidebar */}
            <div className="xl:col-span-1">
              <div className="bg-gray-900 rounded-lg h-[calc(100vh-200px)] lg:h-[600px] flex flex-col">
                {/* Chat Header */}
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageCircle className="w-5 h-5 text-orange-400" />
                    <span className="text-white font-medium">{isLive ? 'Live Chat' : 'Comments'}</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs">
                    {isLive ? (
                      // Live stream status
                      isConnected ? (
                        <span className="flex items-center gap-1 text-green-400">
                          <span className="w-2 h-2 bg-green-400 rounded-full" />
                          Connected
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-yellow-400">
                          <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                          Connecting...
                        </span>
                      )
                    ) : isReplay ? (
                      // Replay/ended stream status
                      <span className="flex items-center gap-1 text-blue-400">
                        <span className="w-2 h-2 bg-blue-400 rounded-full" />
                        Replay
                      </span>
                    ) : (
                      // Ended with no recording
                      <span className="flex items-center gap-1 text-gray-400">
                        <span className="w-2 h-2 bg-gray-400 rounded-full" />
                        Ended
                      </span>
                    )}
                    <span className="text-gray-500">|</span>
                    <span className="flex items-center gap-1 text-gray-400">
                      <Users className="w-3 h-3" />
                      {isLive ? currentViewers : stream.totalViews}
                    </span>
                  </div>
                </div>
                
                {/* Chat Messages */}
                <div 
                  ref={chatContainerRef}
                  className="flex-1 overflow-y-auto p-2 space-y-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent"
                >
                  {allChatMessages.length > 0 ? (
                    allChatMessages.map((msg, index) => (
                      <ChatMessageItem 
                        key={'id' in msg ? msg.id : `live-${index}`}
                        message={msg}
                        isHost={msg.username === stream.host.username}
                      />
                    ))
                  ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500">
                      <Sparkles className="w-8 h-8 mb-2" />
                      <p className="text-sm">No messages yet</p>
                      <p className="text-xs">Be the first to say something!</p>
                    </div>
                  )}
                </div>
                
                {/* Chat Input */}
                {stream.isChatEnabled ? (
                  <form onSubmit={handleSendChat} className="p-3 border-t border-gray-700">
                    {/* Why the last message was not sent. Dismissible, because once it has
                        been read it is only taking up room the chat needs. */}
                    {senderNotice && (
                      <div
                        data-testid="chat-sender-notice"
                        className="mb-2 flex items-start gap-2 rounded-md bg-amber-900/40 px-3 py-2 text-xs text-amber-100"
                      >
                        <span className="flex-1">{senderNotice}</span>
                        <button
                          type="button"
                          aria-label="Dismiss"
                          onClick={() => setSenderNotice(null)}
                          className="text-amber-200 hover:text-white"
                        >
                          ×
                        </button>
                      </div>
                    )}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={chatMessage}
                        onChange={(e) => setChatMessage(e.target.value)}
                        placeholder={isAuthenticated 
                          ? (isLive ? "Send a message..." : "Add a comment...") 
                          : "Sign in to chat"}
                        disabled={!isAuthenticated || (isLive && !isConnected)}
                        onFocus={() => { if (!isAuthenticated) requireAuth('chat'); }}
                        className="flex-1 bg-gray-800 text-white placeholder-gray-400 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 disabled:opacity-50"
                      />
                      <button
                        type="submit"
                        disabled={!isAuthenticated || !chatMessage.trim() || (isLive && !isConnected)}
                        className="px-4 py-2 bg-orange-600 hover:bg-orange-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                      >
                        <Send className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                ) : (
                  <div className="p-3 border-t border-gray-700 text-center">
                    <p className="text-gray-500 text-sm">Chat is disabled for this stream</p>
                  </div>
                )}
              </div>
            </div>
            
          </div>
        </div>
      </div>
      
      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={showLoginPrompt}
        onClose={() => setShowLoginPrompt(false)}
        title="Sign In Required"
        message={loginPromptMessage}
      />

      {/* CSS for floating reactions animation */}
      <style>{`
        @keyframes float-up {
          0% {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
          100% {
            opacity: 0;
            transform: translateY(-100px) scale(1.5);
          }
        }
        .animate-float-up {
          animation: float-up 2s ease-out forwards;
        }
      `}</style>
    </MainLayout>
  );
};

export default LiveStreamWatchPage;
